import { h, Fragment } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { formatMoney } from 'utils/storefront/format-resource';
import useIsInViewport from 'utils/isInViewPort';
import setAttributes from 'utils/set-attributes';
import updateURL from 'utils/update-url';
import { trackPDPToggleClick, trackPDPAddToBag, trackPDPShippingClick, trackPDPBundleClick } from 'utils/tracking';
import { withProductContext } from './product-context';
import VariantSelector from './variant-selector';
import FlyoutPanel, { KeyDetails } from './details-with-flyout-panel';
import ProductQuantity from './product-quantity';
import ProductDetails from './product-details';
import { ShopPay } from './shop-pay';
import ProductAppBlocks from './product-app-blocks';
import MaxOrderMessage from './max-order-message';
import GiftCardForm from './gift-card-form';
import { InventoryMessage, showInventoryMessageFn } from './inventory-message';

const Breadcrumbs = ({ breadcrumbs, primaryCollection, secondaryCollection, thirdCollection }) => {
  const primaryCollectionPath = `${primaryCollection.name}/`;
  const primaryCollectionName = primaryCollection?.name
    ? primaryCollection.name
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

  const primaryCollectionLink = {
    url: primaryCollectionPath,
    title: primaryCollectionName,
  };
  const secondaryCollectionPath = `${secondaryCollection.name}/`;
  const secondaryCollectionName = secondaryCollection?.name
    ? secondaryCollection.name
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

  const secondaryCollectionLink = {
    url: secondaryCollectionPath,
    title: secondaryCollectionName,
  };
  const thirdCollectionPath = `${thirdCollection.name}/`;
  const thirdCollectionName = thirdCollection?.name
    ? thirdCollection.name
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

  const thirdCollectionLink = {
    url: thirdCollectionPath,
    title: thirdCollectionName,
  };

  const breadcrumbsArray = [
    breadcrumbs.home,
    primaryCollectionLink,
    secondaryCollectionLink,
    thirdCollectionLink,
  ].filter((crumb) => crumb && crumb.url);

  const shouldRender =
    !primaryCollectionLink.url.includes('undefined') ||
    !secondaryCollectionLink.url.includes('undefined') ||
    !thirdCollectionLink.url.includes('undefined');

  return shouldRender ? (
    <nav className="breadcrumb mb-2" role="navigation" aria-label="breadcrumbs">
      {breadcrumbsArray.map((breadcrumb, index) => (
        <Fragment key={breadcrumb.title}>
          {index > 0 && (
            <span className="mr-2 ml-2 inline-block" aria-hidden="true">
              /
            </span>
          )}
          <a href={breadcrumb.url} title={breadcrumb.title} className="underline text-link-light-2">
            {breadcrumb.title}
          </a>
        </Fragment>
      ))}
    </nav>
  ) : null;
};

const Price = ({ currentVariant }) => (
  <div className="mb-3.5">
    {currentVariant.compareAtPrice && parseFloat(currentVariant.compareAtPrice.amount) > 0 && (
      <s className="text-headline-tag mr-1">{formatMoney(currentVariant.compareAtPrice)}</s>
    )}
    <span className={`text-headline-tag ${currentVariant.compareAtPrice?.amount ? 'text-red' : ''}`}>
      {formatMoney(currentVariant.price)}
    </span>
  </div>
);

const SingleBundleChildren = ({ items, openFlyout }) =>
  !!items?.length && (
    <div className="pb-2.5">
      <div className="flex items-center justify-between bg-pantone-8 px-5 h-16">
        <span className="flex items-center text-headline-tag">
          <span className="">{window.TMW.Strings.bundle.bundleTitle}</span>
          <span className="">&nbsp;({items?.length})</span>
        </span>

        <button
          className="flyout-trigger flyout-trigger--2 underline text-body-2"
          tabIndex={0}
          title={window.TMW.Strings.product.fullList}
          type="button"
          onClick={openFlyout}
        >
          {window.TMW.Strings.product.fullList}
        </button>
      </div>
      <div className="divide-y divide-grey-4 max-h-[431px] overflow-auto">
        {items?.map((item) => (
          <div className="flex-1 flex flex-col justify-center bg-grey-6 px-5 py-5" key={item.title}>
            <p className="text-headline-tag text-black">{item.title}</p>
            <p className="text-product-tag text-black">{item.shortDescription}</p>
            <p className="text-link-light text-grey-7">{item.options}</p>
          </div>
        ))}
      </div>
    </div>
  );

const AddToCartButton = ({ currentVariant, qty, product, giftForm, isGiftCard }) => {
  const handleAddToCart = (e) => {
    e.preventDefault();
    let attributes = setAttributes(product);

    if (isGiftCard && giftForm.current) {
      const giftFormState = giftForm.current.getFormState();
      if (!giftFormState) {
        return;
      }
      const giftCardAttributes = Object.entries(giftFormState)
        .map(([k, v]) => (v === '' ? undefined : { key: k, value: v }))
        .filter((v) => v);

      attributes = [...attributes, ...giftCardAttributes];
    }

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          ...window.getPayload(currentVariant, qty),
          attributes,
        },
      },
    });
    document.dispatchEvent(addToCartEvent);

    trackPDPAddToBag({
      item: product.title,
      itemId: currentVariant.sku,
      quantity: qty,
    });
  };

  return (
    <button
      type="submit"
      className="atc-button button button--primary w-full h-[50px]"
      disabled={!currentVariant.availableForSale}
      onClick={(e) => handleAddToCart(e)}
    >
      {currentVariant
        ? currentVariant?.availableForSale
          ? window.TMW.Strings.product.addToCart
          : window.TMW.Strings.product.outOfStock
        : window.TMW.Strings.product.unavailable}
    </button>
  );
};

const AddToCartSticky = ({ children }) => (
  <div className="fixed bottom-0 left-0 right-0 z-50 bg-white atc-wrapper">{children}</div>
);

const Shipping = ({ freeShipping: { freeShippingText, standardShipping } }) => {
  const freeShippingTitle = standardShipping
    ? window.TMW.Strings.product.standard_shipping
    : window.TMW.Strings.product.shipping_and_returns;

  const handleShippingFlyoutPanel = (e) => {
    trackPDPShippingClick(freeShippingTitle);
    const openShippingFlyoutPanel = new CustomEvent('shippingFlyoutPanel:open', {
      detail: {
        el: e.target,
      },
    });
    document.dispatchEvent(openShippingFlyoutPanel);
  };

  return (
    <div className="flex items-center gap-2 mt-2">
      {freeShippingText && <p>{freeShippingText}</p>} &bull;
      <button
        className="underline"
        type="button"
        onClick={handleShippingFlyoutPanel}
        tabIndex={0}
        aria-label={freeShippingTitle}
        title={freeShippingTitle}
      >
        {freeShippingTitle}
      </button>
    </div>
  );
};

const PromoMessage = ({ product, promoRef }) => {
  if (
    !product.promo.promo_message_content_01 &&
    !product.promo.promo_message_content_02 &&
    !product.promo.promo_message_title_01 &&
    !product.promo.promo_message_title_02
  )
    return false;

  return (
    <div className="my-5" ref={promoRef}>
      {[1, 2].map((index) => (
        <>
          {product.promo[`promo_message_title_0${index}`] && (
            <div className="bg-pantone-8 px-5 py-4 mb-px">
              <h4 className="text-headline-tag">{product.promo[`promo_message_title_0${index}`]}</h4>
              <div
                className="rte text-body-2 [&>div>p]:!py-0 [&>div>p]:!border-t-0"
                dangerouslySetInnerHTML={{
                  __html: product.promo[`promo_message_content_0${index}`],
                }}
              />
            </div>
          )}
        </>
      ))}
    </div>
  );
};

const KeyDetailsWithFlyout = ({ detailsFlyout }) => {
  const { flyoutTitle, productIcons } = detailsFlyout;

  if (!flyoutTitle || productIcons.length < 1) return null;

  return flyoutTitle ? (
    <Fragment>
      <KeyDetails detailsFlyout={detailsFlyout} />
      <FlyoutPanel detailsFlyout={detailsFlyout} />
    </Fragment>
  ) : null;
};

const ProductMainForm = ({
  currentVariant,
  setCurrentVariant,
  product,
  layout,
  freeShipping,
  detailsFlyout,
  singleBundleItems,
  actualProduct,
  maxOrderQTY,
  templateSuffix,
  breadcrumbs,
  showGiftCardForm,
  primaryCollection,
  secondaryCollection,
  thirdCollection,
  inventoryMessageData
}) => {
  const atcRef = useRef(null);
  const isMobile = layout.bps.below.sm.matches;
  const isInViewPort = useIsInViewport(atcRef);
  const [qty, setQty] = useState(1);
  const isGiftCard = templateSuffix === 'gift_card';
  const giftForm = useRef(null);
  const promoRef = useRef(null);

  useEffect(() => {
    // track promo message links click
    if (promoRef && promoRef.current) {
      const links = promoRef.current.querySelectorAll('a');
      if (!links.length) return;

      links.forEach((link) =>
        link.addEventListener('click', (e) => {
          e.preventDefault();
          trackPDPBundleClick(link.innerText);
          window.location = updateURL(link.href);
        }),
      );
    }
  }, [promoRef]);

  useEffect(() => {
    if (window.Klarna?.OnsiteMessaging) {
      window.Klarna.OnsiteMessaging.refresh();
    }
  }, [currentVariant?.price]);

  const variantSelectors = useMemo(
    () =>
      !product.hasOnlyDefaultVariant &&
      product?.options?.map((option) => (
        <VariantSelector
          currentVariant={currentVariant}
          product={product}
          key={option.name}
          option={option}
          setCurrentVariant={setCurrentVariant}
          isPDP={true}
        />
      )),
    [currentVariant, product],
  );

  const price = useMemo(
    () => <Price currentVariant={currentVariant} id={product.id.split('Product/')[1]} />,
    [currentVariant, product],
  );

  const addToCartButton = useMemo(
    () => (
      <AddToCartButton
        currentVariant={currentVariant}
        qty={qty}
        product={actualProduct}
        maxOrderQTY={maxOrderQTY}
        giftForm={giftForm}
        isGiftCard={isGiftCard}
      />
    ),
    [currentVariant, actualProduct, qty],
  );

  const productQuantity = useMemo(
    () => (
      <ProductQuantity
        quantity={qty}
        changeQuantity={(value) => {
          setQty(value);
          trackPDPToggleClick(`quantity ${value}`);
        }}
        maxOrder={maxOrderQTY?.max_order_qty}
      />
    ),
    [(currentVariant, product, qty)],
  );

  const addToCartSticky = useMemo(
    () => (
      <AddToCartSticky>
        <AddToCartButton
          currentVariant={currentVariant}
          product={actualProduct}
          qty={1}
          giftForm={giftForm}
          isGiftCard={isGiftCard}
        />
      </AddToCartSticky>
    ),
    [currentVariant, actualProduct],
  );

  const showInventoryMessage = showInventoryMessageFn(currentVariant);

  const shipping = useMemo(() => <Shipping freeShipping={freeShipping} />, [freeShipping]);

  const keyDetailsWithFlyout = useMemo(() => <KeyDetailsWithFlyout detailsFlyout={detailsFlyout} />, [detailsFlyout]);

  const promo = useMemo(() => <PromoMessage product={actualProduct} promoRef={promoRef} />, [actualProduct]);

  const singleBundleChildren = useMemo(
    () => <SingleBundleChildren items={singleBundleItems} openFlyout={detailsFlyout.openFlyout} />,
    [product],
  );

  const maxOrderMessage = useMemo(
    () => (
      <MaxOrderMessage maxOrder={maxOrderQTY?.max_order_qty} errorMessage={maxOrderQTY?.error_message} quantity={qty} />
    ),
    [maxOrderQTY, qty],
  );

  const [truncateText, setTruncateText] = useState(false);
  const [descriptionState, setDescriptionState] = useState({
    showFullDescription: false,
    eventFired: false,
  });
  const { showFullDescription } = descriptionState;

  const toggleDescription = (e) => {
    e.preventDefault();

    const updatedState = {
      ...descriptionState,
      showFullDescription: !descriptionState.showFullDescription,
      eventFired: true,
    };

    if (!descriptionState.eventFired) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer?.push({
        event: 'pdp_read_more_click',
        page: 'PDP',
        action: 'Read More Click',
        text: `${actualProduct.title} - ${currentVariant.sku}`,
      });
    }

    setDescriptionState(updatedState);
  };

  const productDescription = useMemo(() => {
    const words = actualProduct.description
      .replace(/<meta charset="utf-8">/g, '')
      .replace(/<br data-mce-fragment="1">/g, '');
    const productDescriptionRef = useRef(null);

    const calculateLineCount = () => {
      if (productDescriptionRef.current) {
        const style = window.getComputedStyle(productDescriptionRef.current);
        const lineHeight = parseFloat(style.lineHeight);
        const paddingTop = parseFloat(style.paddingTop);
        const paddingBottom = parseFloat(style.paddingBottom);
        const borderTop = parseFloat(style.borderTopWidth);
        const borderBottom = parseFloat(style.borderBottomWidth);
        const height = Math.round(productDescriptionRef.current.offsetHeight);
        const count = Math.floor((height - paddingTop - paddingBottom - borderTop - borderBottom) / lineHeight);
        if (count >= 4) {
          productDescriptionRef.current.classList.add('truncate-3-lines');
          setTruncateText(true);
        } else if (count <= 3) {
          setTruncateText(false);
        }
      }
    };

    useEffect(() => {
      calculateLineCount();

      const handleResize = () => {
        productDescriptionRef.current.classList.remove('truncate-3-lines');
        calculateLineCount();
      };

      const throttleAndDebounce = (func, throttleTime, debounceTime) => {
        let timeoutId = null;
        let lastRunTime = 0;

        return function throttledAndDebounced(...args) {
          const context = this;
          const now = Date.now();
          const timeSinceLastRun = now - lastRunTime;
          const shouldThrottle = timeSinceLastRun < throttleTime;

          clearTimeout(timeoutId);

          if (shouldThrottle) {
            timeoutId = setTimeout(() => {
              lastRunTime = Date.now();
              func.apply(context, args);
            }, debounceTime - timeSinceLastRun);
          } else {
            lastRunTime = now;
            func.apply(context, args);
          }
        };
      };

      const throttledAndDebouncedHandleResize = throttleAndDebounce(handleResize, 50, 50);
      window.addEventListener('resize', throttledAndDebouncedHandleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    return (
      <div className="mb-[15px]">
        <div className={`rte text-body-2`}>
          <div
            className={`${truncateText && !descriptionState.showFullDescription ? 'truncate-3-lines' : ''}`}
            ref={productDescriptionRef}
            dangerouslySetInnerHTML={{ __html: words }}
          />
        </div>
        {!truncateText && <div className="min-h-[26px] [&:empty]:hidden"></div>}
        {truncateText && !descriptionState.showFullDescription && (
          <button className="text-link cursor-pointer underline" onClick={toggleDescription}>
            Read More
          </button>
        )}
        {truncateText && descriptionState.showFullDescription && (
          <button className="text-link cursor-pointer underline" onClick={toggleDescription}>
            Read Less
          </button>
        )}
      </div>
    );
  }, [actualProduct, showFullDescription, truncateText]);
  return (
    <form
      method="post"
      action="/cart/add"
      acceptCharset="UTF-8"
      encType="multipart/form-data"
      noValidate="novalidate"
      data-product-form
      className='product-main-form'
    >
      <Breadcrumbs
        breadcrumbs={breadcrumbs}
        primaryCollection={primaryCollection}
        secondaryCollection={secondaryCollection}
        thirdCollection={thirdCollection}
      />
      <h1 className="headline-2 lg:mb-2.5 font-light">{actualProduct.title}</h1>
      {price}
      {product.subtitle && (
        <h2 className={`title-3 ${showInventoryMessage ? 'mb-2' : 'mb-3'}`}>
          {(() => {
            const productSubtitleLowercase = actualProduct.subtitle.toLowerCase();
            return productSubtitleLowercase.charAt(0).toUpperCase() + productSubtitleLowercase.slice(1);
          })()}
        </h2>
      )}
      {showInventoryMessage && <InventoryMessage inventoryMessageData={inventoryMessageData} />}
      <ProductAppBlocks classes='mt-2 mb-2.5' />
      {productDescription}
      {variantSelectors}
      {promo}
      {singleBundleChildren}
      {isGiftCard && showGiftCardForm && <GiftCardForm ref={giftForm} />}
      <div className="flex mt-[20px] mb-1.5">
        {productQuantity}
        <div ref={atcRef} className="w-full atc-wrapper">
          {addToCartButton}
        </div>
      </div>
      {maxOrderQTY && maxOrderMessage}
      {!isGiftCard && currentVariant && (
        <ShopPay price={currentVariant.price} available={currentVariant.availableForSale} />
      )}

      {!isGiftCard && shipping}

      {isMobile && !isInViewPort && addToCartSticky}
      <ProductDetails
        product={actualProduct}
        isBenefits={false}
        specialClass={detailsFlyout.flyoutTitle ? 'last-of-type:border-b-0 last-of-type:mb-0' : 'last-of-type:mb-5'}
      />
      {keyDetailsWithFlyout}
    </form>
  );
};

export default withProductContext(ProductMainForm);
